import React, { useState, useEffect } from "react";
import {
  ChevronDown,
  Upload,
  Download,
  Plus,
  Search,
  Users,
  UserPlus,
  Mail,
  X,
  Check,
  AlertCircle,
  Building2,
  Shield,
  Crown,
  UserX,
  UserCheck,
  Target,
  Calendar,
  TrendingUp,
  Edit2,
  Save,
  RefreshCw,
  BarChart3,
  Zap,
  Wrench,
} from "lucide-react";
import apiService from "../../services/api";
import { useTheme } from "../../contexts/ThemeContext";
import TeamPerformanceBuilder from "../TeamPerformanceBuilder";
import EngineeringCapacityPanel from "../EngineeringCapacityPanel";

const S3_BASE_URL = "https://fibre-crm.s3.eu-west-2.amazonaws.com/";

const getImageUrl = (url) => {
  if (!url) return null;
  return url.startsWith("http://") || url.startsWith("https://")
    ? url
    : `${S3_BASE_URL}${url}`;
};

const UsersTeams = () => {
  const { isDarkMode } = useTheme();

  // Tab state
  const [activeTab, setActiveTab] = useState("members"); // 'members' | 'targets' | 'performance' | 'capacity'
  const [canManageTargets, setCanManageTargets] = useState(false);

  // Team management state
  const [teams, setTeams] = useState([]);
  const [companyUsers, setCompanyUsers] = useState([]);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [teamMembers, setTeamMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isManagement, setIsManagement] = useState(false);

  // Modal state
  const [showAddMemberModal, setShowAddMemberModal] = useState(false);
  const [showCreateTeamModal, setShowCreateTeamModal] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState("member");

  // Appointment targets state
  const [salesTeams, setSalesTeams] = useState([]);
  console.log("sa", salesTeams);
  const [selectedSalesTeam, setSelectedSalesTeam] = useState(null);
  const [salesTeamMembers, setSalesTeamMembers] = useState([]);
  const [targetStats, setTargetStats] = useState(null);
  const [editingUserId, setEditingUserId] = useState(null);
  const [editForm, setEditForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showBulkUpdate, setShowBulkUpdate] = useState(false);
  const [targetsLoading, setTargetsLoading] = useState(false);

  // Member target drawer state
  const [targetDrawerUser, setTargetDrawerUser] = useState(null);
  const [memberTargets, setMemberTargets] = useState({});

  // New team form
  const [newTeam, setNewTeam] = useState({
    name: "",
    description: "",
    type: "custom",
    parentTeamId: null,
  });
  // Add this derived value near your other constants
  const selectedSalesTeamObj = salesTeams.find(
    (t) => t.id === selectedSalesTeam,
  );
  const isWeeklyTeam = ["telemarketing", "sales_engagement"].includes(
    selectedSalesTeamObj?.type,
  );
  const days = [
    "monday",
    "tuesday",
    "wednesday",
    "thursday",
    "friday",
    "saturday",
    "sunday",
  ];
  const dayLabels = {
    monday: "Mon",
    tuesday: "Tue",
    wednesday: "Wed",
    thursday: "Thu",
    friday: "Fri",
    saturday: "Sat",
    sunday: "Sun",
  };

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (selectedTeam) {
      loadTeamMembers(selectedTeam.id);
    }
  }, [selectedTeam]);

  // If we switch to a non-engineering team while on the capacity tab, fall back to members
  useEffect(() => {
    if (activeTab === "capacity" && selectedTeam?.type !== "engineering") {
      setActiveTab("members");
    }
  }, [selectedTeam, activeTab]);

  useEffect(() => {
    loadSalesTeams(); // determine permission early
  }, []);

  useEffect(() => {
    if (!selectedTeam) return;
    (async () => {
      try {
        const res = await apiService.getTeamTargets(selectedTeam.id);
        setMemberTargets(res.data || {});
      } catch (e) {
        console.log("No v2 targets yet — OK");
      }
    })();
  }, [selectedTeam?.id, teamMembers.length]);

  useEffect(() => {
    if (selectedSalesTeam) {
      loadSalesTeamData();
    }
  }, [selectedSalesTeam]);

  // ============= TEAM MANAGEMENT FUNCTIONS =============

  const loadData = async () => {
    try {
      setLoading(true);
      const [teamsData, usersData] = await Promise.all([
        apiService.getAllCompanyTeams(),
        apiService.getCompanyUsers(),
      ]);
      setTeams(teamsData.teams || []);
      setCompanyUsers(usersData.users || []);
      setIsManagement(teamsData.isManagement || false);
      if (teamsData.teams?.length > 0) {
        setSelectedTeam(teamsData.teams[0]);
        loadTeamMembers(teamsData.teams[0].id);
      }
    } catch (error) {
      console.error("Error loading data:", error);
    } finally {
      setLoading(false);
    }
  };

  const loadTeamMembers = async (teamId) => {
    try {
      const response = await apiService.getTeamMembers(teamId);
      setTeamMembers(response.members || []);
    } catch (error) {
      console.error("Error loading team members:", error);
    }
  };

  const handleCreateTeam = async (e) => {
    e.preventDefault();
    if (!newTeam.name.trim()) {
      alert("Please enter a team name");
      return;
    }
    try {
      const response = await apiService.createTeam({
        name: newTeam.name,
        description: newTeam.description,
        type: newTeam.type,
        parentTeamId: newTeam.parentTeamId || null,
      });
      const updatedTeams = [...teams, response.team];
      setTeams(updatedTeams);
      setSelectedTeam(response.team);
      loadTeamMembers(response.team.id);
      setNewTeam({
        name: "",
        description: "",
        type: "custom",
        parentTeamId: null,
      });
      setShowCreateTeamModal(false);
      alert("Team created successfully!");
    } catch (error) {
      console.error("Error creating team:", error);
      alert(error.response?.data?.message || "Error creating team");
    }
  };

  const handleAddExistingUsers = async () => {
    if (!selectedTeam || selectedUsers.length === 0) return;
    try {
      const response = await apiService.bulkAddTeamMembers(
        selectedTeam.id,
        selectedUsers,
        inviteRole,
      );
      alert(`Added ${response.results.added.length} members successfully`);
      await loadTeamMembers(selectedTeam.id);
      await loadData();
      setSelectedUsers([]);
      setShowAddMemberModal(false);
    } catch (error) {
      console.error("Error adding members:", error);
      alert("Error adding members to team");
    }
  };

  const handleInviteNewUser = async () => {
    if (!selectedTeam || !inviteEmail) return;
    try {
      const response = await apiService.inviteToTeam(
        selectedTeam.id,
        inviteEmail,
        inviteRole,
      );
      if (response.type === "added_existing") {
        alert("User added to team successfully (already in company)");
      } else if (response.type === "invited_new") {
        alert("Invitation sent successfully");
      }
      setInviteEmail("");
      setShowAddMemberModal(false);
      await loadTeamMembers(selectedTeam.id);
      await loadData();
    } catch (error) {
      console.error("Error inviting user:", error);
      alert(error.response?.data?.message || "Error inviting user");
    }
  };

  const handleRemoveMember = async (memberId) => {
    if (!window.confirm("Are you sure you want to remove this member?")) return;
    try {
      await apiService.removeTeamMember(selectedTeam.id, memberId);
      await loadTeamMembers(selectedTeam.id);
      await loadData();
    } catch (error) {
      console.error("Error removing member:", error);
    }
  };

  const handleUpdateMemberRole = async (memberId, newRole) => {
    try {
      await apiService.updateTeamMember(selectedTeam.id, memberId, {
        role: newRole,
      });
      await loadTeamMembers(selectedTeam.id);
    } catch (error) {
      console.error("Error updating member role:", error);
    }
  };

  const handleToggleUserStatus = async (user) => {
    const action = user.isActive ? "disable" : "enable";
    if (!window.confirm(`Are you sure you want to ${action} ${user.name}?`))
      return;
    try {
      const response = await apiService.toggleUserStatus(user.id);
      setCompanyUsers((prev) =>
        prev.map((u) =>
          u.id === user.id ? { ...u, isActive: response.user.isActive } : u,
        ),
      );
      setTeamMembers((prev) =>
        prev.map((m) =>
          m.userId === user.id
            ? { ...m, user: { ...m.user, isActive: response.user.isActive } }
            : m,
        ),
      );
    } catch (error) {
      console.error("Error toggling user status:", error);
      alert(error.response?.data?.message || "Error updating user status");
    }
  };

  const canManageTeam = (team) => {
    return isManagement || team?.canManage || team?.isMember;
  };

  const availableUsers = companyUsers.filter(
    (user) =>
      !teamMembers.some((member) => member.userId === user.id) &&
      user.name.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  const toggleUserSelection = (userId) => {
    setSelectedUsers((prev) =>
      prev.includes(userId)
        ? prev.filter((id) => id !== userId)
        : [...prev, userId],
    );
  };

  // ============= APPOINTMENT TARGETS FUNCTIONS =============

  // Replace loadSalesTeams with this:
  const loadSalesTeams = async () => {
    try {
      setTargetsLoading(true);

      // Reuse the team context endpoint to know my role per team
      const ctxRes = await apiService.getUserTeamContext();
      const calendarRes = await apiService.getAllCompanyTeams();

      const ctx = ctxRes.context || {};
      const allTeams = calendarRes.teams || [];
      console.log("t", allTeams);

      // Sales-related teams only
      const salesLike = allTeams.filter((t) =>
        ["sales", "sales_engagement", "telemarketing"].includes(t.type),
      );

      const isAdmin = ["admin", "super_admin"].includes(ctx.userRole);
      // Management team membership grants full access
      const isManagement =
        Array.isArray(ctx.teamTypes) && ctx.teamTypes.includes("management");
      console.log("m", isManagement);

      let visibleTeams = [];
      if (isAdmin || isManagement) {
        visibleTeams = salesLike;
      } else {
        // Only teams where the user is a manager
        const managedTypes = (ctx.teams || [])
          .filter(
            (t) =>
              t.userRole === "manager" &&
              ["sales", "sales_engagement", "telemarketing"].includes(t.type),
          )
          .map((t) => t.id);
        visibleTeams = salesLike.filter((t) => managedTypes.includes(t.id));
      }
      console.log("xx", visibleTeams);
      setSalesTeams(visibleTeams);
      setCanManageTargets(visibleTeams.length > 0);

      if (visibleTeams.length > 0 && !selectedSalesTeam) {
        setSelectedSalesTeam(visibleTeams[0].id);
      } else if (visibleTeams.length === 0) {
        setSelectedSalesTeam(null);
      }
    } catch (error) {
      console.error("Error loading sales teams:", error);
    } finally {
      setTargetsLoading(false);
    }
  };

  const loadSalesTeamData = async () => {
    try {
      setTargetsLoading(true);
      const [membersResponse, statsResponse] = await Promise.all([
        apiService.getTeamAppointmentTargets(selectedSalesTeam, {
          includeStats: true,
        }),
        apiService.getAppointmentTargetStats({ teamId: selectedSalesTeam }),
      ]);

      setSalesTeamMembers(membersResponse.data.members || []);
      setTargetStats(statsResponse.data || null);
    } catch (error) {
      console.error("Error loading sales team data:", error);
    } finally {
      setTargetsLoading(false);
    }
  };

  const handleEditMemberTargets = (member) => {
    setEditingUserId(member.userId);
    setEditForm({
      userId: member.userId,
      weeklyDefaults: { ...member.targets.weeklyDefaults },
      enabled: member.targets.enabled,
    });
  };

  const handleSaveMemberTargets = async () => {
    try {
      setSaving(true);
      await apiService.updateUserAppointmentTargets(editingUserId, editForm);
      await loadSalesTeamData();
      setEditingUserId(null);
      setEditForm(null);
    } catch (error) {
      console.error("Error saving targets:", error);
      alert("Failed to save targets");
    } finally {
      setSaving(false);
    }
  };

  const handleBulkUpdate = async (updateData) => {
    try {
      setSaving(true);
      const updates = salesTeamMembers.map((member) => ({
        userId: member.userId,
        ...updateData,
      }));

      await apiService.bulkUpdateAppointmentTargets({ updates });
      await loadSalesTeamData();
      setShowBulkUpdate(false);
    } catch (error) {
      console.error("Error bulk updating targets:", error);
      alert("Failed to update targets");
    } finally {
      setSaving(false);
    }
  };

  // ============= STYLING HELPERS =============

  const pageBg = isDarkMode
    ? "bg-[#0A0B0F] text-white"
    : "bg-gray-50 text-gray-900";
  const cardBg = isDarkMode
    ? "bg-[#0C0E12] border-gray-700"
    : "bg-white border-gray-200";
  const subCardBg = isDarkMode ? "bg-[#13161B]" : "bg-gray-50";
  const inputCls = isDarkMode
    ? "bg-[#0A0B0F] border-gray-700 text-white focus:border-[#427BCA]"
    : "bg-white border-gray-300 text-gray-900 focus:border-[#427BCA]";
  const mutedText = isDarkMode ? "text-[#94979C]" : "text-gray-500";
  const theadBg = isDarkMode ? "bg-[#13161B]" : "bg-gray-50";
  const thText = isDarkMode ? "text-[#94979C]" : "text-gray-500";
  const rowBorder = isDarkMode
    ? "border-gray-700 hover:bg-gray-800/30"
    : "border-gray-200 hover:bg-gray-50";
  const modalBg = isDarkMode
    ? "bg-[#0C0E12] border-gray-700"
    : "bg-white border-gray-200";
  const avatarBg = isDarkMode ? "bg-[#373A41]" : "bg-gray-200";
  const avatarText = isDarkMode ? "text-[#94979C]" : "text-gray-500";

  if (loading) {
    return (
      <div
        className={`min-h-screen font-inter flex items-center justify-center ${pageBg}`}
      >
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-[#427BCA] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className={mutedText}>Loading teams...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen font-inter ${pageBg}`}>
      <div className="p-4 lg:p-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <h1 className="text-3xl font-bold">Users & Teams</h1>
              {isManagement && (
                <div className="flex items-center gap-2 px-3 py-1 bg-amber-500/20 border border-amber-500/30 rounded-lg">
                  <Crown size={16} className="text-amber-400" />
                  <span className="text-sm text-amber-400 font-medium">
                    Management Access
                  </span>
                </div>
              )}
            </div>
            <p className={mutedText}>
              {isManagement
                ? "You have full access to manage all company teams"
                : "Manage your team members and team structure"}
            </p>
          </div>

          <div className="flex gap-3">
            {activeTab === "members" && isManagement && (
              <button
                onClick={() => setShowCreateTeamModal(true)}
                className={`flex items-center gap-2 px-4 py-2 border rounded-lg transition-colors ${
                  isDarkMode
                    ? "bg-[#0C0E12] border-gray-700 hover:bg-gray-700"
                    : "bg-white border-gray-300 hover:bg-gray-50"
                }`}
              >
                <Plus size={16} />
                Create Team
              </button>
            )}
            {activeTab === "members" &&
              selectedTeam &&
              canManageTeam(selectedTeam) && (
                <button
                  onClick={() => setShowAddMemberModal(true)}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg text-white"
                  style={{
                    background:
                      "radial-gradient(50% 50% at 50% 50%, #427BCA 0%, #2F578F 100%)",
                  }}
                >
                  <UserPlus size={16} />
                  Add Member
                </button>
              )}
            {activeTab === "targets" && (
              <button
                onClick={() => setShowBulkUpdate(true)}
                className="flex items-center gap-2 px-4 py-2 rounded-lg text-white"
                style={{
                  background:
                    "radial-gradient(50% 50% at 50% 50%, #427BCA 0%, #2F578F 100%)",
                }}
              >
                <Users size={16} />
                Bulk Update
              </button>
            )}
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="flex gap-2 mb-6">
          <button
            onClick={() => setActiveTab("members")}
            className={`flex items-center gap-2 px-6 py-3 rounded-lg transition-colors font-medium ${
              activeTab === "members"
                ? "bg-[#427BCA] text-white"
                : isDarkMode
                  ? "bg-[#0C0E12] text-gray-400 hover:bg-gray-700 border border-gray-700"
                  : "bg-white text-gray-600 hover:bg-gray-100 border border-gray-200"
            }`}
          >
            <Users size={20} />
            Team Members
          </button>

          {canManageTargets && (
            <button
              onClick={() => setActiveTab("targets")}
              className={`flex items-center gap-2 px-6 py-3 rounded-lg transition-colors font-medium ${
                activeTab === "targets"
                  ? "bg-[#427BCA] text-white"
                  : isDarkMode
                    ? "bg-[#0C0E12] text-gray-400 hover:bg-gray-700 border border-gray-700"
                    : "bg-white text-gray-600 hover:bg-gray-100 border border-gray-200"
              }`}
            >
              <Target size={20} />
              Appointment Targets
            </button>
          )}

          {selectedTeam?.type === "engineering" && (
            <button
              onClick={() => setActiveTab("capacity")}
              className={`flex items-center gap-2 px-6 py-3 rounded-lg transition-colors font-medium ${
                activeTab === "capacity"
                  ? "bg-[#427BCA] text-white"
                  : isDarkMode
                    ? "bg-[#0C0E12] text-gray-400 hover:bg-gray-700 border border-gray-700"
                    : "bg-white text-gray-600 hover:bg-gray-100 border border-gray-200"
              }`}
            >
              <Wrench size={20} />
              Install Capacity
            </button>
          )}

          {/* {isManagement && (
            <button
              onClick={() => setActiveTab("performance")}
              className={`flex items-center gap-2 px-6 py-3 rounded-lg transition-colors font-medium ${
                activeTab === "performance"
                  ? "bg-[#427BCA] text-white"
                  : isDarkMode
                    ? "bg-[#0C0E12] text-gray-400 hover:bg-gray-700 border border-gray-700"
                    : "bg-white text-gray-600 hover:bg-gray-100 border border-gray-200"
              }`}
            >
              <BarChart3 size={20} />
              Performance Builder
            </button>
          )} */}
        </div>

        {/* ============= TEAM MEMBERS TAB ============= */}
        {activeTab === "members" && (
          <>
            {/* Teams Grid */}
            {teams.length === 0 ? (
              <div
                className={`text-center py-16 rounded-xl border mb-6 ${cardBg}`}
              >
                <Building2
                  size={48}
                  className="mx-auto mb-4 text-[#427BCA] opacity-50"
                />
                <h3 className="text-xl font-semibold mb-2">No teams yet</h3>
                <p className={`${mutedText} mb-4`}>
                  Create your first team to get started
                </p>
                {isManagement && (
                  <button
                    onClick={() => setShowCreateTeamModal(true)}
                    className="inline-flex items-center gap-2 px-6 py-3 bg-[#427BCA] text-white rounded-lg hover:bg-[#3668a8] transition-colors"
                  >
                    <Plus size={16} />
                    Create Your First Team
                  </button>
                )}
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                {teams.map((team) => (
                  <div
                    key={team.id}
                    onClick={() => {
                      setSelectedTeam(team);
                      loadTeamMembers(team.id);
                    }}
                    className={`p-4 rounded-xl border cursor-pointer transition-all relative ${
                      selectedTeam?.id === team.id
                        ? "border-[#427BCA] bg-[#427BCA]/10"
                        : isDarkMode
                          ? "border-gray-700 bg-[#0C0E12] hover:border-gray-600"
                          : "border-gray-200 bg-white hover:border-gray-300"
                    }`}
                  >
                    {team.isMember && (
                      <div className="absolute top-2 right-2">
                        <div
                          className="w-2 h-2 rounded-full bg-green-500"
                          title="You are a member"
                        />
                      </div>
                    )}

                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-12 h-12 rounded-full bg-[#427BCA]/20 flex items-center justify-center">
                        <Users size={24} className="text-[#427BCA]" />
                      </div>
                      <div className="flex-1">
                        <h3 className="font-semibold">{team.name}</h3>
                        <p className={`text-sm capitalize ${mutedText}`}>
                          {team.type.replace(/_/g, " ")}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center justify-between text-sm">
                      <span className={mutedText}>
                        {team.stats?.totalMembers || 0}{" "}
                        {team.stats?.totalMembers === 1 ? "member" : "members"}
                      </span>
                      {team.isMember && (
                        <span className="text-[#427BCA] font-medium capitalize">
                          {team.userRole?.replace("_", " ")}
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Team Members Table */}
            {selectedTeam && (
              <div className={`rounded-xl border ${cardBg}`}>
                <div
                  className={`p-6 border-b ${isDarkMode ? "border-gray-700" : "border-gray-200"}`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="flex items-center gap-3 mb-1">
                        <h2 className="text-xl font-semibold">
                          {selectedTeam.name}
                        </h2>
                        {selectedTeam.isMember && (
                          <span className="px-2 py-1 text-xs bg-green-500/20 text-green-400 rounded border border-green-500/30">
                            Member
                          </span>
                        )}
                        {isManagement && !selectedTeam.isMember && (
                          <span className="px-2 py-1 text-xs bg-amber-500/20 text-amber-400 rounded border border-amber-500/30 flex items-center gap-1">
                            <Crown size={12} />
                            Management
                          </span>
                        )}
                      </div>
                      <p className={mutedText}>Team members and their roles</p>
                    </div>
                    <div className={`text-sm ${mutedText}`}>
                      {teamMembers.length}{" "}
                      {teamMembers.length === 1 ? "member" : "members"}
                    </div>
                  </div>
                </div>

                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className={theadBg}>
                      <tr
                        className={`border-b ${isDarkMode ? "border-gray-700" : "border-gray-200"}`}
                      >
                        <th
                          className={`text-left py-4 px-4 font-medium ${thText}`}
                        >
                          Member
                        </th>
                        <th
                          className={`text-left py-4 px-4 font-medium ${thText}`}
                        >
                          Email
                        </th>
                        <th
                          className={`text-left py-4 px-4 font-medium ${thText}`}
                        >
                          Role
                        </th>
                        {isManagement && (
                          <th
                            className={`text-left py-4 px-4 font-medium ${thText}`}
                          >
                            Status
                          </th>
                        )}
                        {canManageTeam(selectedTeam) && (
                          <th
                            className={`text-right py-4 px-4 font-medium ${thText}`}
                          >
                            Actions
                          </th>
                        )}
                        <th
                          className={`text-left py-4 px-4 font-medium ${thText}`}
                        >
                          Targets
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {teamMembers.length === 0 ? (
                        <tr>
                          <td
                            colSpan={
                              (canManageTeam(selectedTeam) ? 1 : 0) +
                              (isManagement ? 1 : 0) +
                              3
                            }
                            className={`py-8 text-center ${mutedText}`}
                          >
                            No members yet.
                            {canManageTeam(selectedTeam) &&
                              ' Click "Add Member" to get started.'}
                          </td>
                        </tr>
                      ) : (
                        teamMembers.map((member) => (
                          <tr
                            key={member.id}
                            className={`border-b ${rowBorder}`}
                          >
                            <td className="py-4 px-4">
                              <div className="flex items-center gap-3">
                                <div
                                  className={`w-10 h-10 rounded-full flex items-center justify-center ${avatarBg}`}
                                >
                                  {member.user?.profileImageUrl ? (
                                    <img
                                      src={getImageUrl(
                                        member.user.profileImageUrl,
                                      )}
                                      alt={member.user.name}
                                      className="w-full h-full rounded-full object-cover"
                                    />
                                  ) : (
                                    <span
                                      className={`text-sm font-semibold ${avatarText}`}
                                    >
                                      {member.user?.name
                                        ?.substring(0, 2)
                                        .toUpperCase()}
                                    </span>
                                  )}
                                </div>
                                <span className="font-medium">
                                  {member.user?.name}
                                </span>
                              </div>
                            </td>
                            <td className={`py-4 px-4 ${mutedText}`}>
                              {member.user?.email}
                            </td>
                            <td className="py-4 px-4">
                              {canManageTeam(selectedTeam) ? (
                                <select
                                  value={member.role}
                                  onChange={(e) =>
                                    handleUpdateMemberRole(
                                      member.id,
                                      e.target.value,
                                    )
                                  }
                                  className={`border rounded px-3 py-1 text-sm ${
                                    isDarkMode
                                      ? "bg-[#0C0E12] border-gray-700 text-white"
                                      : "bg-white border-gray-300 text-gray-900"
                                  }`}
                                >
                                  <option value="manager">Manager</option>
                                  <option value="team_lead">Team Lead</option>
                                  <option value="senior_member">
                                    Senior Member
                                  </option>
                                  <option value="member">Member</option>
                                  <option value="observer">Observer</option>
                                </select>
                              ) : (
                                <span className="capitalize">
                                  {member.role.replace("_", " ")}
                                </span>
                              )}
                            </td>
                            {isManagement && (
                              <td className="py-4 px-4">
                                {member.user?.isActive !== false ? (
                                  <span className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-green-500/20 text-green-400 rounded border border-green-500/30">
                                    <span className="w-1.5 h-1.5 rounded-full bg-green-400" />
                                    Active
                                  </span>
                                ) : (
                                  <span className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-red-500/20 text-red-400 rounded border border-red-500/30">
                                    <span className="w-1.5 h-1.5 rounded-full bg-red-400" />
                                    Disabled
                                  </span>
                                )}
                              </td>
                            )}
                            {canManageTeam(selectedTeam) && (
                              <td className="py-4 px-4 text-right">
                                <div className="flex items-center justify-end gap-3">
                                  {isManagement && (
                                    <button
                                      onClick={() =>
                                        handleToggleUserStatus(member.user)
                                      }
                                      title={
                                        member.user?.isActive !== false
                                          ? "Disable user"
                                          : "Enable user"
                                      }
                                      className={`transition-colors text-sm font-medium flex items-center gap-1 ${
                                        member.user?.isActive !== false
                                          ? "text-amber-400 hover:text-amber-300"
                                          : "text-green-400 hover:text-green-300"
                                      }`}
                                    >
                                      {member.user?.isActive !== false ? (
                                        <>
                                          <UserX size={14} />
                                          Disable
                                        </>
                                      ) : (
                                        <>
                                          <UserCheck size={14} />
                                          Enable
                                        </>
                                      )}
                                    </button>
                                  )}
                                  <button
                                    onClick={() =>
                                      handleRemoveMember(member.id)
                                    }
                                    className="text-red-400 hover:text-red-300 transition-colors text-sm font-medium"
                                  >
                                    Remove
                                  </button>
                                </div>
                              </td>
                            )}
                            {selectedTeam && (
                              <td className="py-4 px-4">
                                <button
                                  onClick={() =>
                                    setTargetDrawerUser(member.user)
                                  }
                                  className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-[#427BCA]/15 text-[#427BCA] text-xs font-medium border border-[#427BCA]/30 hover:bg-[#427BCA]/25"
                                >
                                  <Zap size={11} />
                                  {memberTargets.memberOverrides?.[
                                    member.userId
                                  ]?.length || 0}{" "}
                                  override(s)
                                </button>
                              </td>
                            )}
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </>
        )}

        {/* ============= APPOINTMENT TARGETS TAB ============= */}
        {activeTab === "targets" && (
          <div className="space-y-6">
            {/* Sales Team Selector */}
            <div className={`rounded-xl border p-6 ${cardBg}`}>
              <label className="block text-sm font-medium mb-2">
                Select Sales Team
              </label>
              <select
                value={selectedSalesTeam || ""}
                onChange={(e) => setSelectedSalesTeam(e.target.value)}
                className={`w-full max-w-md border rounded-lg px-4 py-2 ${inputCls}`}
              >
                {salesTeams.length === 0 && (
                  <option value="">No sales teams found</option>
                )}
                {salesTeams.map((team) => (
                  <option key={team.id} value={team.id}>
                    {team.name} ({team.stats.activeMembers} members)
                  </option>
                ))}
              </select>
            </div>

            {targetsLoading ? (
              <div className="flex items-center justify-center p-12">
                <RefreshCw className="animate-spin text-[#427BCA]" size={32} />
              </div>
            ) : selectedSalesTeam ? (
              <>
                {/* This Week's Stats Summary */}
                {targetStats?.stats &&
                  (() => {
                    const activeStats = targetStats.stats.filter(
                      (s) => s.targets?.enabled !== false,
                    );
                    const weekTarget = activeStats.reduce(
                      (sum, s) => sum + (s.summary?.totalTarget || 0),
                      0,
                    );
                    const weekActual = activeStats.reduce(
                      (sum, s) => sum + (s.summary?.totalActual || 0),
                      0,
                    );
                    const membersWithTarget = activeStats.filter(
                      (s) => (s.summary?.totalTarget || 0) > 0,
                    );
                    const avgAchievement =
                      membersWithTarget.length > 0
                        ? Math.round(
                            membersWithTarget.reduce(
                              (sum, s) =>
                                sum + (s.summary?.overallPercentage || 0),
                              0,
                            ) / membersWithTarget.length,
                          )
                        : 0;
                    const onTrackCount = membersWithTarget.filter(
                      (s) => (s.summary?.overallPercentage || 0) >= 80,
                    ).length;

                    return (
                      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                        <StatCard
                          title="Next Week Target"
                          value={weekTarget}
                          icon={Target}
                          color="blue"
                          isDarkMode={isDarkMode}
                        />
                        <StatCard
                          title="Next Week Booked"
                          value={weekActual}
                          icon={Calendar}
                          color="green"
                          isDarkMode={isDarkMode}
                        />
                        <StatCard
                          title="Avg Achievement"
                          value={`${avgAchievement}%`}
                          icon={TrendingUp}
                          color="purple"
                          isDarkMode={isDarkMode}
                        />
                        <StatCard
                          title="On Track (≥80%)"
                          value={`${onTrackCount}/${membersWithTarget.length}`}
                          icon={Users}
                          color="orange"
                          isDarkMode={isDarkMode}
                        />
                      </div>
                    );
                  })()}

                {/* Members List with Targets */}
                <div className={`rounded-xl border ${cardBg}`}>
                  <div
                    className={`p-6 border-b ${isDarkMode ? "border-gray-700" : "border-gray-200"}`}
                  >
                    <h3 className="text-lg font-semibold">
                      Team Member Targets
                    </h3>
                  </div>

                  <div
                    className={`divide-y ${isDarkMode ? "divide-gray-700" : "divide-gray-200"}`}
                  >
                    {salesTeamMembers.length === 0 ? (
                      <div className={`p-8 text-center ${mutedText}`}>
                        No members found in this sales team
                      </div>
                    ) : (
                      salesTeamMembers
                        .filter((m) => m.user?.isActive !== false)
                        .map((member) => {
                          const isEditing = editingUserId === member.userId;
                          const memberStat = targetStats?.stats?.find(
                            (s) => s.userId === member.userId,
                          );
                          const weekSummary = memberStat?.summary;
                          const weekPct = weekSummary?.overallPercentage || 0;

                          return (
                            <div key={member.userId} className="p-6">
                              {/* Member Header */}
                              <div className="flex items-center justify-between mb-4">
                                <div className="flex items-center gap-3">
                                  <div
                                    className={`w-10 h-10 rounded-full flex items-center justify-center ${avatarBg}`}
                                  >
                                    {member.user.profileImageUrl ? (
                                      <img
                                        src={getImageUrl(
                                          member.user.profileImageUrl,
                                        )}
                                        alt={member.user.name}
                                        className="w-full h-full rounded-full object-cover"
                                      />
                                    ) : (
                                      <span
                                        className={`text-sm font-semibold ${avatarText}`}
                                      >
                                        {member.user.name
                                          .substring(0, 2)
                                          .toUpperCase()}
                                      </span>
                                    )}
                                  </div>
                                  <div>
                                    <div className="font-medium">
                                      {member.user.name}
                                    </div>
                                    <div className={`text-sm ${mutedText}`}>
                                      {member.user.email}
                                    </div>
                                  </div>
                                </div>

                                <div className="flex items-center gap-3">
                                  {weekSummary && (
                                    <div className="text-right">
                                      <div className="text-sm font-medium">
                                        {weekSummary.totalActual} /{" "}
                                        {weekSummary.totalTarget} next week
                                      </div>
                                      <div
                                        className={`text-sm ${
                                          weekPct >= 100
                                            ? "text-green-400"
                                            : weekPct >= 75
                                              ? "text-yellow-400"
                                              : "text-red-400"
                                        }`}
                                      >
                                        {weekPct}%
                                      </div>
                                    </div>
                                  )}

                                  {!isEditing ? (
                                    <button
                                      onClick={() =>
                                        handleEditMemberTargets(member)
                                      }
                                      className={`p-2 rounded-lg transition-colors ${
                                        isDarkMode
                                          ? "hover:bg-gray-700"
                                          : "hover:bg-gray-100"
                                      }`}
                                    >
                                      <Edit2 size={16} />
                                    </button>
                                  ) : (
                                    <div className="flex gap-2">
                                      <button
                                        onClick={handleSaveMemberTargets}
                                        disabled={saving}
                                        className="p-2 bg-green-500 hover:bg-green-600 rounded-lg transition-colors disabled:opacity-50"
                                      >
                                        <Save size={16} />
                                      </button>
                                      <button
                                        onClick={() => {
                                          setEditingUserId(null);
                                          setEditForm(null);
                                        }}
                                        className={`p-2 rounded-lg transition-colors ${
                                          isDarkMode
                                            ? "hover:bg-gray-700"
                                            : "hover:bg-gray-100"
                                        }`}
                                      >
                                        <X size={16} />
                                      </button>
                                    </div>
                                  )}
                                </div>
                              </div>

                              {/* Weekly Targets */}
                              {isWeeklyTeam ? (
                                // Single weekly number for telemarketing / sales_engagement
                                <div className="flex items-center gap-4">
                                  <span className={`text-sm ${mutedText}`}>
                                    Weekly target
                                  </span>
                                  {isEditing ? (
                                    <input
                                      type="number"
                                      min="0"
                                      max="999"
                                      value={Object.values(
                                        editForm.weeklyDefaults,
                                      ).reduce(
                                        (s, v) => s + (parseInt(v) || 0),
                                        0,
                                      )}
                                      onChange={(e) => {
                                        const val =
                                          parseInt(e.target.value) || 0;
                                        // Distribute evenly Mon–Fri, zero Sat/Sun
                                        const perDay = Math.floor(val / 5);
                                        const remainder = val % 5;
                                        setEditForm({
                                          ...editForm,
                                          weeklyDefaults: {
                                            monday:
                                              perDay + (remainder > 0 ? 1 : 0),
                                            tuesday:
                                              perDay + (remainder > 1 ? 1 : 0),
                                            wednesday:
                                              perDay + (remainder > 2 ? 1 : 0),
                                            thursday:
                                              perDay + (remainder > 3 ? 1 : 0),
                                            friday:
                                              perDay + (remainder > 4 ? 1 : 0),
                                            saturday: 0,
                                            sunday: 0,
                                          },
                                        });
                                      }}
                                      className={`w-24 text-center border rounded px-2 py-1 text-sm ${inputCls}`}
                                    />
                                  ) : (
                                    <div className="text-lg font-semibold text-[#427BCA]">
                                      {Object.values(
                                        member.targets.weeklyDefaults,
                                      ).reduce(
                                        (s, v) => s + (parseInt(v) || 0),
                                        0,
                                      )}
                                      <span
                                        className={`text-sm font-normal ml-1 ${mutedText}`}
                                      >
                                        / week
                                      </span>
                                    </div>
                                  )}
                                </div>
                              ) : (
                                // Daily grid for sales teams
                                <div className="grid grid-cols-7 gap-2">
                                  {days.map((day) => {
                                    const target = isEditing
                                      ? editForm.weeklyDefaults[day]
                                      : member.targets.weeklyDefaults[day];
                                    return (
                                      <div key={day} className="text-center">
                                        <div
                                          className={`text-xs font-medium mb-1 ${mutedText}`}
                                        >
                                          {dayLabels[day]}
                                        </div>
                                        {isEditing ? (
                                          <input
                                            type="number"
                                            min="0"
                                            max="20"
                                            value={editForm.weeklyDefaults[day]}
                                            onChange={(e) =>
                                              setEditForm({
                                                ...editForm,
                                                weeklyDefaults: {
                                                  ...editForm.weeklyDefaults,
                                                  [day]:
                                                    parseInt(e.target.value) ||
                                                    0,
                                                },
                                              })
                                            }
                                            className={`w-full text-center border rounded px-2 py-1 text-sm ${inputCls}`}
                                          />
                                        ) : (
                                          <div
                                            className={`py-1 px-2 rounded ${
                                              target > 0
                                                ? "bg-[#427BCA]/20 text-[#427BCA]"
                                                : isDarkMode
                                                  ? "bg-gray-700 text-gray-400"
                                                  : "bg-gray-100 text-gray-500"
                                            }`}
                                          >
                                            {target}
                                          </div>
                                        )}
                                      </div>
                                    );
                                  })}
                                </div>
                              )}

                              {!member.targets.enabled && (
                                <div className="mt-3 flex items-center gap-2 text-yellow-400 text-sm">
                                  <AlertCircle size={14} />
                                  Targets disabled for this user
                                </div>
                              )}
                            </div>
                          );
                        })
                    )}
                  </div>
                </div>
              </>
            ) : (
              <div className={`text-center py-16 rounded-xl border ${cardBg}`}>
                <BarChart3
                  size={48}
                  className="mx-auto mb-4 text-[#427BCA] opacity-50"
                />
                <h3 className="text-xl font-semibold mb-2">
                  Select a Sales Team
                </h3>
                <p className={mutedText}>
                  Choose a sales team to view and manage appointment targets
                </p>
              </div>
            )}
          </div>
        )}

        {/* ============= PERFORMANCE BUILDER TAB ============= */}
        {activeTab === "performance" && isManagement && (
          <TeamPerformanceBuilder />
        )}

        {/* ============= INSTALL CAPACITY TAB ============= */}
        {activeTab === "capacity" && selectedTeam?.type === "engineering" && (
          <EngineeringCapacityPanel
            team={selectedTeam}
            isManagement={isManagement}
          />
        )}

        {/* ============= CREATE TEAM MODAL ============= */}
        {showCreateTeamModal && isManagement && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className={`rounded-xl border max-w-2xl w-full ${modalBg}`}>
              <div
                className={`p-6 border-b flex items-center justify-between ${
                  isDarkMode ? "border-gray-700" : "border-gray-200"
                }`}
              >
                <h2 className="text-xl font-semibold">Create New Team</h2>
                <button
                  onClick={() => {
                    setShowCreateTeamModal(false);
                    setNewTeam({
                      name: "",
                      description: "",
                      type: "custom",
                      parentTeamId: null,
                    });
                  }}
                  className={
                    isDarkMode
                      ? "text-gray-400 hover:text-white"
                      : "text-gray-500 hover:text-gray-900"
                  }
                >
                  <X size={20} />
                </button>
              </div>

              <form onSubmit={handleCreateTeam} className="p-6 space-y-4">
                <div>
                  <label
                    className={`block text-sm font-medium mb-2 ${isDarkMode ? "text-white" : "text-gray-700"}`}
                  >
                    Team Name <span className="text-red-400">*</span>
                  </label>
                  <input
                    type="text"
                    value={newTeam.name}
                    onChange={(e) =>
                      setNewTeam({ ...newTeam, name: e.target.value })
                    }
                    placeholder="e.g., Sales Team, Marketing Department"
                    className={`w-full border rounded-lg px-4 py-2 focus:outline-none ${inputCls}`}
                    required
                  />
                </div>

                <div>
                  <label
                    className={`block text-sm font-medium mb-2 ${isDarkMode ? "text-white" : "text-gray-700"}`}
                  >
                    Description
                  </label>
                  <textarea
                    value={newTeam.description}
                    onChange={(e) =>
                      setNewTeam({ ...newTeam, description: e.target.value })
                    }
                    placeholder="Brief description of the team's purpose..."
                    rows={3}
                    className={`w-full border rounded-lg px-4 py-2 focus:outline-none ${inputCls}`}
                  />
                </div>

                <div>
                  <label
                    className={`block text-sm font-medium mb-2 ${isDarkMode ? "text-white" : "text-gray-700"}`}
                  >
                    Team Type
                  </label>
                  <select
                    value={newTeam.type}
                    onChange={(e) =>
                      setNewTeam({ ...newTeam, type: e.target.value })
                    }
                    className={`w-full border rounded-lg px-4 py-2 focus:outline-none ${inputCls}`}
                  >
                    <option value="sales">Sales</option>
                    <option value="telemarketing">Telemarketing</option>
                    <option value="sales_engagement">Sales Engagement</option>
                    <option value="management">Management</option>
                    <option value="support">Support</option>
                    <option value="operations">Operations</option>
                    <option value="finance">Finance</option>
                    <option value="engineering">Engineering</option>
                    <option value="product">Product</option>
                    <option value="custom">Custom</option>
                  </select>
                </div>

                <div>
                  <label
                    className={`block text-sm font-medium mb-2 ${isDarkMode ? "text-white" : "text-gray-700"}`}
                  >
                    Parent Team (Optional)
                  </label>
                  <select
                    value={newTeam.parentTeamId || ""}
                    onChange={(e) =>
                      setNewTeam({
                        ...newTeam,
                        parentTeamId: e.target.value || null,
                      })
                    }
                    className={`w-full border rounded-lg px-4 py-2 focus:outline-none ${inputCls}`}
                  >
                    <option value="">None (Top-level team)</option>
                    {teams.map((team) => (
                      <option key={team.id} value={team.id}>
                        {team.name}
                      </option>
                    ))}
                  </select>
                  <p
                    className={`text-sm mt-1 ${isDarkMode ? "text-[#94979C]" : "text-gray-500"}`}
                  >
                    Select a parent team to create a sub-team
                  </p>
                </div>

                <div className="flex justify-end gap-3 pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowCreateTeamModal(false);
                      setNewTeam({
                        name: "",
                        description: "",
                        type: "custom",
                        parentTeamId: null,
                      });
                    }}
                    className={`px-6 py-2 border rounded-lg transition-colors ${
                      isDarkMode
                        ? "border-gray-700 hover:bg-gray-700"
                        : "border-gray-300 hover:bg-gray-100 text-gray-700"
                    }`}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-6 py-2 bg-[#427BCA] text-white rounded-lg hover:bg-[#3668a8] transition-colors"
                  >
                    Create Team
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* ============= ADD MEMBER MODAL ============= */}
        {showAddMemberModal && canManageTeam(selectedTeam) && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div
              className={`rounded-xl border max-w-2xl w-full max-h-[90vh] overflow-y-auto ${modalBg}`}
            >
              <div
                className={`p-6 border-b flex items-center justify-between sticky top-0 ${
                  isDarkMode
                    ? "border-gray-700 bg-[#0C0E12]"
                    : "border-gray-200 bg-white"
                }`}
              >
                <h2 className="text-xl font-semibold">
                  Add Members to {selectedTeam?.name}
                </h2>
                <button
                  onClick={() => setShowAddMemberModal(false)}
                  className={
                    isDarkMode
                      ? "text-gray-400 hover:text-white"
                      : "text-gray-500 hover:text-gray-900"
                  }
                >
                  <X size={20} />
                </button>
              </div>

              <div className="p-6">
                {/* Invite by Email */}
                <div className="mb-6">
                  <h3 className="font-medium mb-3">Invite New User</h3>
                  <div className="flex gap-3">
                    <input
                      type="email"
                      placeholder="Enter email address"
                      value={inviteEmail}
                      onChange={(e) => setInviteEmail(e.target.value)}
                      className={`flex-1 border rounded-lg px-4 py-2 ${inputCls}`}
                    />
                    <select
                      value={inviteRole}
                      onChange={(e) => setInviteRole(e.target.value)}
                      className={`border rounded-lg px-4 py-2 ${inputCls}`}
                    >
                      <option value="manager">Manager</option>
                      <option value="team_lead">Team Lead</option>
                      <option value="senior_member">Senior Member</option>
                      <option value="member">Member</option>
                      <option value="observer">Observer</option>
                    </select>
                    <button
                      onClick={handleInviteNewUser}
                      className="px-6 py-2 bg-[#427BCA] text-white rounded-lg hover:bg-[#3668a8] transition-colors"
                    >
                      <Mail size={16} />
                    </button>
                  </div>
                  <p
                    className={`text-sm mt-2 ${isDarkMode ? "text-[#94979C]" : "text-gray-500"}`}
                  >
                    If user exists in company, they'll be added directly.
                    Otherwise, an invitation will be sent.
                  </p>
                </div>

                {/* Divider */}
                <div className="relative my-6">
                  <div className="absolute inset-0 flex items-center">
                    <div
                      className={`w-full border-t ${
                        isDarkMode ? "border-gray-700" : "border-gray-200"
                      }`}
                    ></div>
                  </div>
                  <div className="relative flex justify-center text-sm">
                    <span
                      className={`px-2 ${
                        isDarkMode
                          ? "bg-[#0C0E12] text-[#94979C]"
                          : "bg-white text-gray-500"
                      }`}
                    >
                      OR
                    </span>
                  </div>
                </div>

                {/* Add Existing Users */}
                <div>
                  <h3 className="font-medium mb-3">
                    Add Existing Company Members
                  </h3>
                  <div className="mb-4">
                    <div className="relative">
                      <Search
                        className={`absolute left-3 top-1/2 transform -translate-y-1/2 ${
                          isDarkMode ? "text-[#94979C]" : "text-gray-400"
                        }`}
                        size={18}
                      />
                      <input
                        type="text"
                        placeholder="Search users..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className={`w-full border rounded-lg pl-10 pr-4 py-2 ${inputCls}`}
                      />
                    </div>
                  </div>

                  <div className="max-h-64 overflow-y-auto space-y-2">
                    {availableUsers.length === 0 ? (
                      <div
                        className={`text-center py-8 ${
                          isDarkMode ? "text-[#94979C]" : "text-gray-400"
                        }`}
                      >
                        <AlertCircle
                          size={32}
                          className="mx-auto mb-2 opacity-50"
                        />
                        <p>No available users found</p>
                      </div>
                    ) : (
                      availableUsers.map((user) => (
                        <div
                          key={user.id}
                          onClick={() => toggleUserSelection(user.id)}
                          className={`p-3 rounded-lg border cursor-pointer transition-all ${
                            selectedUsers.includes(user.id)
                              ? "border-[#427BCA] bg-[#427BCA]/10"
                              : isDarkMode
                                ? "border-gray-700 hover:border-gray-600"
                                : "border-gray-200 hover:border-gray-300"
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <div
                                className={`w-10 h-10 rounded-full flex items-center justify-center ${avatarBg}`}
                              >
                                {user.profileImageUrl ? (
                                  <img
                                    src={getImageUrl(user.profileImageUrl)}
                                    alt={user.name}
                                    className="w-full h-full rounded-full object-cover"
                                  />
                                ) : (
                                  <span
                                    className={`text-sm font-semibold ${avatarText}`}
                                  >
                                    {user.name.substring(0, 2).toUpperCase()}
                                  </span>
                                )}
                              </div>
                              <div>
                                <div className="font-medium">{user.name}</div>
                                <div
                                  className={`text-sm ${
                                    isDarkMode
                                      ? "text-[#94979C]"
                                      : "text-gray-500"
                                  }`}
                                >
                                  {user.email}
                                </div>
                              </div>
                            </div>
                            {selectedUsers.includes(user.id) && (
                              <Check size={20} className="text-[#427BCA]" />
                            )}
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </div>

              <div
                className={`p-6 border-t flex justify-between items-center sticky bottom-0 ${
                  isDarkMode
                    ? "border-gray-700 bg-[#0C0E12]"
                    : "border-gray-200 bg-white"
                }`}
              >
                <div
                  className={`text-sm ${
                    isDarkMode ? "text-[#94979C]" : "text-gray-500"
                  }`}
                >
                  {selectedUsers.length} user
                  {selectedUsers.length !== 1 ? "s" : ""} selected
                </div>
                <div className="flex gap-3">
                  <button
                    onClick={() => {
                      setShowAddMemberModal(false);
                      setSelectedUsers([]);
                    }}
                    className={`px-6 py-2 border rounded-lg transition-colors ${
                      isDarkMode
                        ? "border-gray-700 hover:bg-gray-700"
                        : "border-gray-300 hover:bg-gray-100 text-gray-700"
                    }`}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleAddExistingUsers}
                    disabled={selectedUsers.length === 0}
                    className="px-6 py-2 bg-[#427BCA] text-white rounded-lg hover:bg-[#3668a8] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Add {selectedUsers.length} Member
                    {selectedUsers.length !== 1 ? "s" : ""}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ============= BULK UPDATE MODAL ============= */}
        {showBulkUpdate && (
          <BulkUpdateModal
            onClose={() => setShowBulkUpdate(false)}
            onUpdate={handleBulkUpdate}
            isWeeklyTeam={isWeeklyTeam}
            isDarkMode={isDarkMode}
            days={days}
            dayLabels={dayLabels}
            inputCls={inputCls}
            modalBg={modalBg}
            saving={saving}
          />
        )}

        {/* ============= MEMBER TARGET DRAWER ============= */}
        {targetDrawerUser && (
          <MemberTargetDrawer
            teamId={selectedTeam.id}
            user={targetDrawerUser}
            memberOverrides={
              memberTargets.memberOverrides?.[targetDrawerUser.id] || []
            }
            teamDefaults={memberTargets.teamDefaults || []}
            onClose={() => setTargetDrawerUser(null)}
            onSaved={async () => {
              const res = await apiService.getTeamTargets(selectedTeam.id);
              setMemberTargets(res.data || {});
            }}
            isDarkMode={isDarkMode}
          />
        )}
      </div>
    </div>
  );
};

// ============= STAT CARD COMPONENT =============
const StatCard = ({ title, value, icon: Icon, color, isDarkMode }) => {
  const colors = {
    blue: "bg-blue-500/20 text-blue-400",
    green: "bg-green-500/20 text-green-400",
    purple: "bg-purple-500/20 text-purple-400",
    orange: "bg-orange-500/20 text-orange-400",
  };

  const cardBg = isDarkMode
    ? "bg-[#0C0E12] border-gray-700"
    : "bg-white border-gray-200";
  const mutedText = isDarkMode ? "text-[#94979C]" : "text-gray-500";

  return (
    <div className={`rounded-xl border p-4 ${cardBg}`}>
      <div className="flex items-center justify-between mb-2">
        <span className={mutedText}>{title}</span>
        <div className={`p-2 rounded-lg ${colors[color]}`}>
          <Icon size={20} />
        </div>
      </div>
      <div className="text-2xl font-bold">{value}</div>
    </div>
  );
};

// ============= BULK UPDATE MODAL COMPONENT =============
const BulkUpdateModal = ({
  onClose,
  onUpdate,
  isDarkMode,
  days,
  dayLabels,
  inputCls,
  modalBg,
  isWeeklyTeam,
  saving,
}) => {
  const [bulkTargets, setBulkTargets] = useState({
    monday: 4,
    tuesday: 4,
    wednesday: 4,
    thursday: 4,
    friday: 4,
    saturday: 0,
    sunday: 0,
  });

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className={`rounded-xl border max-w-2xl w-full ${modalBg}`}>
        <div
          className={`p-6 border-b ${isDarkMode ? "border-gray-700" : "border-gray-200"}`}
        >
          <h3 className="text-xl font-semibold">
            Bulk Update All Team Members
          </h3>
          <p
            className={`text-sm mt-1 ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}
          >
            Set the same weekly targets for all team members
          </p>
        </div>

        <div className="p-6">
          {isWeeklyTeam ? (
            <div className="flex items-center gap-4 py-2">
              <label className="text-sm font-medium">
                Weekly target for all members
              </label>
              <input
                type="number"
                min="0"
                max="999"
                value={Object.values(bulkTargets).reduce(
                  (s, v) => s + (parseInt(v) || 0),
                  0,
                )}
                onChange={(e) => {
                  const val = parseInt(e.target.value) || 0;
                  const perDay = Math.floor(val / 5);
                  const remainder = val % 5;
                  setBulkTargets({
                    monday: perDay + (remainder > 0 ? 1 : 0),
                    tuesday: perDay + (remainder > 1 ? 1 : 0),
                    wednesday: perDay + (remainder > 2 ? 1 : 0),
                    thursday: perDay + (remainder > 3 ? 1 : 0),
                    friday: perDay + (remainder > 4 ? 1 : 0),
                    saturday: 0,
                    sunday: 0,
                  });
                }}
                className={`w-24 text-center border rounded-lg px-2 py-2 ${inputCls}`}
              />
            </div>
          ) : (
            <div className="grid grid-cols-7 gap-3">
              {days.map((day) => (
                <div key={day}>
                  <label
                    className={`block text-xs font-medium mb-2 text-center ${
                      isDarkMode ? "text-gray-300" : "text-gray-700"
                    }`}
                  >
                    {dayLabels[day]}
                  </label>
                  <input
                    type="number"
                    min="0"
                    max="20"
                    value={bulkTargets[day]}
                    onChange={(e) =>
                      setBulkTargets({
                        ...bulkTargets,
                        [day]: parseInt(e.target.value) || 0,
                      })
                    }
                    className={`w-full text-center border rounded-lg px-2 py-2 ${inputCls}`}
                  />
                </div>
              ))}
            </div>
          )}
        </div>

        <div
          className={`p-6 border-t ${isDarkMode ? "border-gray-700" : "border-gray-200"} flex justify-end gap-3`}
        >
          <button
            onClick={onClose}
            disabled={saving}
            className={`px-4 py-2 border rounded-lg transition-colors disabled:opacity-50 ${
              isDarkMode
                ? "border-gray-700 hover:bg-gray-700"
                : "border-gray-300 hover:bg-gray-100"
            }`}
          >
            Cancel
          </button>
          <button
            onClick={() => onUpdate({ weeklyDefaults: bulkTargets })}
            disabled={saving}
            className="px-4 py-2 bg-[#427BCA] text-white rounded-lg hover:bg-[#3668a8] transition-colors disabled:opacity-50 flex items-center gap-2"
          >
            {saving && <RefreshCw size={16} className="animate-spin" />}
            {saving ? "Updating..." : "Update All Members"}
          </button>
        </div>
      </div>
    </div>
  );
};

const MemberTargetDrawer = ({
  teamId,
  user,
  memberOverrides,
  teamDefaults,
  onClose,
  onSaved,
  isDarkMode,
}) => {
  const [granularity, setGranularity] = useState("daily");
  const [saving, setSaving] = useState(false);

  const cardBg = isDarkMode
    ? "bg-[#0C0E12] border-gray-700"
    : "bg-white border-gray-200";
  const muted = isDarkMode ? "text-[#94979C]" : "text-gray-500";

  const overridesByMetric = {};
  for (const o of memberOverrides) {
    if (!overridesByMetric[o.metricId]) overridesByMetric[o.metricId] = {};
    overridesByMetric[o.metricId][o.granularity] = o;
  }

  const upsert = async (metricId, payload) => {
    setSaving(true);
    try {
      await apiService.upsertMemberTarget(teamId, user.id, {
        metricId,
        granularity,
        periodType: "weekday_pattern",
        ...payload,
      });
      await onSaved();
    } catch (e) {
      alert(e?.response?.data?.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-end">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div
        className={`relative h-full w-full max-w-lg shadow-2xl border-l flex flex-col ${cardBg}`}
      >
        <div className="flex items-center justify-between p-5 border-b border-gray-700/50">
          <div>
            <h2 className="font-semibold">{user.name}</h2>
            <p className={`text-xs ${muted}`}>Target overrides</p>
          </div>
          <button onClick={onClose} className="p-2">
            <X size={16} />
          </button>
        </div>

        {/* Granularity tabs */}
        <div className="flex gap-1 px-5 pt-4">
          {["daily", "weekly", "monthly"].map((g) => (
            <button
              key={g}
              onClick={() => setGranularity(g)}
              className={`px-3 py-1 text-xs rounded ${
                granularity === g
                  ? "bg-[#427BCA] text-white"
                  : isDarkMode
                    ? "text-gray-400"
                    : "text-gray-600"
              }`}
            >
              {g[0].toUpperCase() + g.slice(1)}
            </button>
          ))}
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {teamDefaults
            .filter((t) => t.granularity === granularity)
            .map((def) => {
              const override = overridesByMetric[def.metricId]?.[granularity];
              return (
                <TargetOverrideRow
                  key={`${def.metricId}_${granularity}`}
                  teamDefault={def}
                  override={override}
                  granularity={granularity}
                  onSave={(p) => upsert(def.metricId, p)}
                  isDarkMode={isDarkMode}
                  saving={saving}
                />
              );
            })}
          {teamDefaults.filter((t) => t.granularity === granularity).length ===
            0 && (
            <div className={`text-center py-10 ${muted}`}>
              No team-level {granularity} targets configured yet.
              <br />
              Set them up in the Performance Builder first.
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const TargetOverrideRow = ({
  teamDefault,
  override,
  granularity,
  onSave,
  isDarkMode,
  saving,
}) => {
  const [weekdayTargets, setWeekdayTargets] = useState(
    override?.weekdayTargets ||
      teamDefault.weekdayTargets || {
        mon: 0,
        tue: 0,
        wed: 0,
        thu: 0,
        fri: 0,
        sat: 0,
        sun: 0,
      },
  );
  const [fixedValue, setFixedValue] = useState(
    override?.fixedValue ?? teamDefault.fixedValue ?? 0,
  );
  const periodType =
    override?.periodType || teamDefault.periodType || "weekday_pattern";

  const cardBg = isDarkMode ? "bg-[#13161B]" : "bg-gray-50";
  const inputBg = isDarkMode
    ? "bg-[#0A0B0F] border-gray-700 text-white"
    : "bg-white border-gray-300 text-gray-900";

  return (
    <div className={`rounded-lg p-4 ${cardBg}`}>
      <div className="flex items-center justify-between mb-2">
        <p className="font-medium text-sm">{teamDefault.metricId}</p>
        {override && (
          <span className="text-xs px-2 py-0.5 rounded bg-[#427BCA]/20 text-[#427BCA]">
            Override active
          </span>
        )}
      </div>

      {periodType === "weekday_pattern" && granularity === "daily" && (
        <div className="grid grid-cols-7 gap-1 mb-3">
          {["mon", "tue", "wed", "thu", "fri", "sat", "sun"].map((d) => (
            <div key={d} className="text-center">
              <div className="text-[10px] text-gray-500 mb-1">
                {d.toUpperCase()}
              </div>
              <input
                type="number"
                min="0"
                value={weekdayTargets[d] || 0}
                onChange={(e) =>
                  setWeekdayTargets({
                    ...weekdayTargets,
                    [d]: parseInt(e.target.value) || 0,
                  })
                }
                className={`w-full text-center text-sm rounded border px-1 py-1 ${inputBg}`}
              />
            </div>
          ))}
        </div>
      )}

      {periodType === "fixed" && (
        <input
          type="number"
          min="0"
          value={fixedValue}
          onChange={(e) => setFixedValue(parseInt(e.target.value) || 0)}
          className={`w-full rounded border px-3 py-2 text-sm mb-3 ${inputBg}`}
          placeholder={`e.g. ${granularity === "weekly" ? "10 per week" : "40 per month"}`}
        />
      )}

      <div className="flex gap-2">
        <button
          onClick={() =>
            onSave(
              periodType === "weekday_pattern"
                ? { weekdayTargets, periodType }
                : { fixedValue, periodType },
            )
          }
          disabled={saving}
          className="px-3 py-1.5 bg-[#427BCA] text-white rounded text-xs font-medium disabled:opacity-50"
        >
          {saving
            ? "Saving..."
            : override
              ? "Update override"
              : "Create override"}
        </button>
        {override && (
          <button
            onClick={() =>
              onSave(
                periodType === "weekday_pattern"
                  ? { weekdayTargets: teamDefault.weekdayTargets, periodType }
                  : { fixedValue: teamDefault.fixedValue, periodType },
              )
            }
            className="px-3 py-1.5 text-xs text-gray-500 hover:text-gray-300"
          >
            Reset to team default
          </button>
        )}
      </div>
    </div>
  );
};

export default UsersTeams;
